import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import TextField from "components/TextField";
import Select from "components/Select";
import { getCountries } from "actions/countries";
import { getUserInformation } from "actions/user";
import { SET_CURRENCY } from "actions/currency";
import {
  SUPPORTED_COUNTRIES,
  COUNTRY_FALLBACKS,
  getCurrencyForCountry,
  getCountryForCurrency,
  isSupportedCountry,
} from "pages/dtf-transfers/checkout/currency";
import {
  FormSection,
  SectionTitle,
  SectionHeader,
  FieldGrid,
  FieldRow,
  FieldNote,
} from "styles/pages/dtf-transfers/checkout";

const getStatesForCountry = (countries, countryCode) =>
  _.find(countries, (o) => o.code2 === countryCode)?.states || [];

const InformationStep = ({ formData, fieldErrors = {}, onChange }) => {
  const dispatch = useDispatch();
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const fullName = useSelector((state) => state?.auth?.profile?.FullName);
  const countries = useSelector((state) => state?.countries?.data);
  const reduxCurrency = useSelector((state) => state?.currency?.value || "CAD");
  const [statesList, setStatesList] = useState([]);
  const hasPrefilled = useRef(false);

  const supportedCountries = SUPPORTED_COUNTRIES.map(
    (code) =>
      countries?.find((country) => country.code2 === code) ||
      COUNTRY_FALLBACKS[code],
  );

  const isCanada = formData.country === "CA";
  const stateLabel = isCanada ? "Province" : "State";
  const postalLabel = isCanada ? "Postal code" : "ZIP code";

  useEffect(() => {
    const loadCountries = async () => {
      if (!countries?.length) {
        await dispatch(getCountries());
      }
    };

    loadCountries();
  }, [dispatch, countries?.length]);

  useEffect(() => {
    const loadUserDetails = async () => {
      if (!emailid || hasPrefilled.current || !countries?.length) return;

      const user = await dispatch(getUserInformation(emailid));
      if (user?.error) return;

      hasPrefilled.current = true;

      const profile = user?.User || {};
      const country = getCountryForCurrency(reduxCurrency);
      const states = getStatesForCountry(countries, country);
      const stateProvince = states?.some(
        (state) => state.code === profile.StateProvince,
      )
        ? profile.StateProvince
        : "";

      setStatesList(states || []);

      onChange({
        email: profile.Email || emailid || "",
        firstName:
          profile.FullName?.split(" ")?.[0] || fullName?.split(" ")?.[0] || "",
        lastName:
          profile.FullName?.split(" ")?.slice(1).join(" ") ||
          fullName?.split(" ")?.slice(1).join(" ") ||
          "",
        company: profile.Company || "",
        addressLineOne: profile.AddressLineOne || "",
        addressLineTwo: profile.AddressLineTwo || "",
        city: profile.City || "",
        stateProvince,
        country,
        zipPostal: profile.ZipPostal || "",
        phone: profile.Phone || "",
      });
    };

    loadUserDetails();
  }, [dispatch, emailid, fullName, countries, onChange, reduxCurrency]);

  useEffect(() => {
    if (!countries?.length) return;

    const countryCode = isSupportedCountry(formData.country)
      ? formData.country
      : "CA";
    const states = getStatesForCountry(countries, countryCode);
    setStatesList(states);

    if (formData.country !== countryCode) {
      onChange({
        country: countryCode,
        stateProvince: "",
      });
      dispatch({
        type: SET_CURRENCY,
        currency: getCurrencyForCountry(countryCode),
      });
    }
  }, [formData.country, countries, onChange, dispatch]);

  const updateField = (field, value) => {
    onChange({ [field]: value });
  };

  const handleCountryChange = (country) => {
    const states = getStatesForCountry(countries, country);
    setStatesList(states);
    onChange({
      country,
      stateProvince: "",
    });
    dispatch({
      type: SET_CURRENCY,
      currency: getCurrencyForCountry(country),
    });
  };

  return (
    <>
      <FormSection>
        <SectionHeader>
          <SectionTitle>Contact</SectionTitle>
        </SectionHeader>

        <FieldGrid>
          <FieldRow>
            <TextField
              fullWidth
              size="small"
              label="Email"
              value={formData.email}
              error={!!fieldErrors.email}
              onChange={(email) => updateField("email", email)}
            />
          </FieldRow>
        </FieldGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Shipping address</SectionTitle>

        <FieldGrid>
          <FieldRow>
            <Select
              size="small"
              label="Country/Region"
              options={supportedCountries}
              labelKey="name"
              valueKey="code2"
              value={{ code2: formData.country }}
              error={!!fieldErrors.country}
              onChange={handleCountryChange}
            />
          </FieldRow>

          <FieldGrid $columns={2}>
            <TextField
              fullWidth
              size="small"
              label="First name"
              value={formData.firstName}
              error={!!fieldErrors.firstName}
              onChange={(firstName) => updateField("firstName", firstName)}
            />
            <TextField
              fullWidth
              size="small"
              label="Last name"
              value={formData.lastName}
              error={!!fieldErrors.lastName}
              onChange={(lastName) => updateField("lastName", lastName)}
            />
          </FieldGrid>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              label="Company (optional)"
              value={formData.company}
              onChange={(company) => updateField("company", company)}
            />
          </FieldRow>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              label="Address"
              value={formData.addressLineOne}
              error={!!fieldErrors.addressLineOne}
              onChange={(addressLineOne) =>
                updateField("addressLineOne", addressLineOne)
              }
            />
          </FieldRow>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              label="Apartment, suite, etc. (optional)"
              value={formData.addressLineTwo}
              onChange={(addressLineTwo) =>
                updateField("addressLineTwo", addressLineTwo)
              }
            />
          </FieldRow>

          <FieldGrid $columns={3}>
            <TextField
              fullWidth
              size="small"
              label="City"
              value={formData.city}
              error={!!fieldErrors.city}
              onChange={(city) => updateField("city", city)}
            />

            <Select
              size="small"
              label={stateLabel}
              options={statesList}
              labelKey="name"
              valueKey="code"
              value={{ code: formData.stateProvince }}
              error={!!fieldErrors.stateProvince}
              onChange={(stateProvince) =>
                updateField("stateProvince", stateProvince)
              }
            />

            <TextField
              fullWidth
              size="small"
              label={postalLabel}
              value={formData.zipPostal}
              error={!!fieldErrors.zipPostal}
              onChange={(zipPostal) => updateField("zipPostal", zipPostal)}
            />
          </FieldGrid>

          <FieldRow>
            <TextField
              fullWidth
              size="small"
              label="Phone (optional)"
              value={formData.phone}
              onChange={(phone) => updateField("phone", phone)}
            />
          </FieldRow>
        </FieldGrid>
      </FormSection>
    </>
  );
};

export default InformationStep;
